import { CheckCircleTwoTone, ClockCircleTwoTone, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { timeManagementChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why does going to bed and waking up at the same time matter?',
    answer: (
      <>
        A consistent sleep schedule keeps your energy and focus steady throughout the day. Without it, you'll feel groggy and struggle to
        start tasks. This same consistency is what makes it easier to keep up with{' '}
        <Link to="/adulting-101/household-care">Household Care</Link> and other daily routines.
      </>
    ),
  },
  {
    question: 'I always run out of time. What am I doing wrong?',
    answer: (
      <>
        Usually it means you're not planning ahead, getting distracted, or underestimating how long tasks take. Try writing down your tasks
        the night before and giving each one a realistic time estimate, then padding it a little. This kind of planning is also key to{' '}
        <Link to="/adulting-101/financial-literacy">Financial Literacy</Link>, where budgeting your money works the same way as budgeting
        your time.
      </>
    ),
  },
  {
    question: 'How do I know what to prioritize?',
    answer: (
      <>
        Ask what has the soonest deadline and what would cause the biggest problem if it didn't get done. Do those first. This skill
        overlaps with <Link to="/adulting-101/problem-solving">Problem Solving</Link>, where breaking things down helps you figure out what
        matters most.
      </>
    ),
  },
  {
    question: 'How do I stop procrastinating?',
    answer: (
      <>
        Break big or unpleasant tasks into smaller steps so the first step feels easy to start. Remove distractions before you begin, and
        give yourself a small reward when you finish. Following through this way is what builds{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link> — people learn they can count on you.
      </>
    ),
  },
  {
    question: 'How realistic should my deadlines be?',
    answer: (
      <>
        Set deadlines you can actually meet, with a little buffer for the unexpected. Overly ambitious deadlines just set you up to fail and
        feel discouraged. If you're juggling a job or chores too, this connects with the planning skills in{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: 'How do I limit distractions like my phone?',
    answer: (
      <>
        Put your phone in another room, use app timers, or turn on "do not disturb" while you work. Save social media and messaging for
        after your tasks are done. Being mindful of distractions is closely tied to{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link> — noticing what pulls your attention and choosing to
        refocus.
      </>
    ),
  },
  {
    question: 'Is it bad to plan fun activities before finishing my tasks?',
    answer: (
      <>
        It's tempting, but doing fun things first often means tasks get rushed or skipped entirely. Save your relaxation and hangouts with{' '}
        <Link to="/adulting-101/friends-and-family">Friends and Family</Link> for after your responsibilities are handled — you'll enjoy it
        more without the guilt hanging over you.
      </>
    ),
  },
  {
    question: 'Why should I review my day and adjust my plan for tomorrow?',
    answer: (
      <>
        Reviewing what worked and what didn't helps you improve your planning over time. It's the same kind of reflection used in{' '}
        <Link to="/adulting-101/spiritual-awareness">Spiritual Awareness</Link> — taking a moment to notice patterns in your life so you can
        keep growing.
      </>
    ),
  },
]

export function Adulting101TimeManagementPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Time Management</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #e6f7ff 0%, #ffffff 220px)' }}>
        <Title level={2}>Good time management keeps your responsibilities from piling up on you.</Title>
        <Paragraph type="secondary">
          Do you always feel like there aren't enough hours in the day? Learning to plan and prioritize can help you get more done with less
          stress.
        </Paragraph>

        <Card
          title={
            <Space>
              <ClockCircleTwoTone />
              Time Management Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you're managing your time like a responsible adult!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Time management is a skill you build daily. The more you practice planning and prioritizing, the easier it gets.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Time Management
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
